const INTRO = 'Thank you for visiting today. Here is a summary of the key instructions:';
const CLOSING = 'Please reach out if you have any questions or concerns.';

const BULLET_RE = /^(?:[-•*]|\d{1,2}\.)\s+(.*)$/;
const FIND_BELOW_RE = /^(?:please\s+)?find\s+below\s+a\s+summary\s+of\s+the\s+plan\b/i;

export const PATIENT_INSTRUCTION_HEADINGS = [
  'Medications',
  'Testing',
  'Safety precautions',
  'When to call',
  'Follow-up',
  'Next steps',
] as const;

type PiSection = (typeof PATIENT_INSTRUCTION_HEADINGS)[number];

const ALIASES: Array<[string, PiSection]> = [
  ['medicines and symptom care', 'Medications'],
  ['medication instructions', 'Medications'],
  ['medication changes', 'Medications'],
  ['medications and supplements', 'Medications'],
  ['medications', 'Medications'],
  ['medicines', 'Medications'],
  ['testing and evaluation', 'Testing'],
  ['diagnostic tests', 'Testing'],
  ['tests', 'Testing'],
  ['testing', 'Testing'],
  ['safety precautions', 'Safety precautions'],
  ['safety recommendations', 'Safety precautions'],
  ['safety', 'Safety precautions'],
  ['when to call/er', 'When to call'],
  ['when to call / er', 'When to call'],
  ['when to seek care', 'When to call'],
  ['when to call', 'When to call'],
  ['call/er', 'When to call'],
  ['follow-up', 'Follow-up'],
  ['follow up', 'Follow-up'],
  ['followup', 'Follow-up'],
  ['contact info / next steps', 'Next steps'],
  ['contact info', 'Next steps'],
  ['next steps', 'Next steps'],
  ['other instructions', 'Next steps'],
  ['other', 'Next steps'],
  ['referrals', 'Next steps'],
  ['lifestyle', 'Next steps'],
];

function stripDecor(s: string): string {
  return s
    .trim()
    .replace(/^\*+\s*/, '')
    .replace(/\s*\*+$/, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .replace(/^#+\s*/, '')
    .trim();
}

function normalizeName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[*_`]/g, '')
    .replace(/\s+/g, ' ')
    .replace(/:$/, '')
    .trim();
}

export function canonicalPatientInstructionHeading(line: string | null | undefined): PiSection | null {
  if (!line) {
    return null;
  }
  let t = stripDecor(line);
  const bullet = t.match(BULLET_RE);
  if (bullet) {
    t = (bullet[1] || '').trim();
  }
  const colon = t.indexOf(':');
  const name = colon > 0 && colon <= 48 ? t.slice(0, colon).trim() : t;
  if (name.split(/\s+/).filter(Boolean).length > 6) {
    return null;
  }
  const n = normalizeName(name);
  for (const [alias, section] of ALIASES) {
    if (n === alias) {
      return section;
    }
  }
  return null;
}

function isGreeting(plain: string): boolean {
  return /^dear\s+/i.test(plain);
}

function isIntro(plain: string): boolean {
  const t = plain.replace(/[.:]+$/, '').trim();
  if (FIND_BELOW_RE.test(t)) {
    return true;
  }
  const n = t.toLowerCase();
  return (
    n.startsWith('thank you for visiting') ||
    n === 'here is a summary of the key instructions' ||
    n === 'here is a summary of the plan'
  );
}

function isClosingStart(plain: string): boolean {
  const n = plain.toLowerCase().replace(/[.:]+$/, '').trim();
  return (
    n.startsWith('please reach out') ||
    n.startsWith('best regards') ||
    n.startsWith('kind regards') ||
    n.startsWith('warm regards') ||
    n === 'sincerely' ||
    n.startsWith('sincerely')
  );
}

function isStandardClosing(plain: string): boolean {
  return plain.toLowerCase().replace(/[.:]+$/, '').trim().startsWith('please reach out');
}

function isNarrative(t: string): boolean {
  if (t.endsWith('.') || t.endsWith('?') || t.endsWith('!')) {
    return t.split(/\s+/).length >= 8 || t.length >= 40;
  }
  return false;
}

function addContent(dest: string[], line: string): void {
  const t = stripDecor(line).trim();
  if (!t) {
    return;
  }
  const bullet = t.match(BULLET_RE);
  if (bullet) {
    const body = (bullet[1] || '').trim();
    if (body) {
      dest.push('- ' + body);
    }
    return;
  }
  dest.push(isNarrative(t) ? t : '- ' + t);
}

function ensureDearComma(greeting: string): string {
  const g = greeting.trim();
  return g.endsWith(',') ? g : g + ',';
}

function resolveGreeting(greeting: string | null, patientName?: string | null): string {
  const name = (patientName ?? '').trim();
  const usableName = !!name && !/^patient$/i.test(name);
  if (!greeting) {
    return usableName ? `Dear ${name},` : 'Dear Patient,';
  }
  if (usableName && /^dear\s+patient,?$/i.test(greeting)) {
    return `Dear ${name},`;
  }
  return ensureDearComma(greeting);
}

/**
 * Display-only rewrite of Patient Instructions into the letter layout:
 * Dear name, thank-you intro, six topic headings with "- " bullets, closing.
 */
export function foldPatientInstructionsForDisplay(
  text: string | null | undefined,
  patientName?: string | null
): string {
  const raw = (text ?? '').replace(/\r/g, '').trim();
  if (!raw || /^n\/a$/i.test(raw)) {
    return raw;
  }

  let greeting: string | null = null;
  let sawIntro = false;
  const closingLines: string[] = [];
  let inClosing = false;
  let current: PiSection | null = null;
  const bodies = new Map<PiSection, string[]>();
  for (const heading of PATIENT_INSTRUCTION_HEADINGS) {
    bodies.set(heading, []);
  }
  const pending: string[] = [];

  for (const rawLine of raw.split('\n')) {
    const trimmed = (rawLine ?? '').trim();
    if (!trimmed) {
      continue;
    }
    const plain = stripDecor(trimmed);

    if (isGreeting(plain)) {
      if (!greeting) {
        greeting = ensureDearComma(plain);
      }
      continue;
    }
    if (isIntro(plain)) {
      sawIntro = true;
      continue;
    }
    if (isClosingStart(plain) || inClosing) {
      inClosing = true;
      if (!isStandardClosing(plain)) {
        closingLines.push(plain);
      }
      continue;
    }

    const heading = canonicalPatientInstructionHeading(plain);
    if (heading) {
      current = heading;
      const colon = stripDecor(plain).indexOf(':');
      if (colon >= 0) {
        const after = stripDecor(plain).slice(colon + 1).trim();
        if (after) {
          addContent(bodies.get(heading) ?? [], after);
        }
      }
      continue;
    }

    if (current) {
      addContent(bodies.get(current) ?? [], plain);
    } else {
      pending.push(plain);
    }
  }

  if (pending.length) {
    const next = bodies.get('Next steps') ?? [];
    for (const line of pending) {
      addContent(next, line);
    }
    bodies.set('Next steps', next);
  }

  const any = PATIENT_INSTRUCTION_HEADINGS.some((h) => (bodies.get(h) ?? []).length > 0);
  if (!any && !sawIntro && !greeting) {
    return raw;
  }

  const parts: string[] = [];
  parts.push(resolveGreeting(greeting, patientName), '', INTRO);

  for (const heading of PATIENT_INSTRUCTION_HEADINGS) {
    const lines = bodies.get(heading) ?? [];
    if (!lines.length) {
      continue;
    }
    parts.push('', heading + ':', ...lines);
  }

  parts.push('', CLOSING);
  if (closingLines.length) {
    parts.push('', ...closingLines);
  }
  return parts.join('\n').trim();
}
